import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {HomologyParameters, AmbiguousConstructPreference} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {AssemblyParametersControlsLayout} from 'molbio/bulk-assembly/setup-modal/controls/AssemblyParametersControlsLayout';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

interface HomologyParametersControlsProps {
  homologyParameters: HomologyParameters;
  patchHomologyParameters: (newParams: Partial<HomologyParameters>) => void;
  isReadOnly: boolean;
}

const AMBIGUOUS_CONSTRUCT_PREFERENCE_TO_DISPLAY = {
  [AmbiguousConstructPreference.USE_LARGER_FRAGMENT_IN_FIRST_BIN]: 'Use larger fragment in first bin',
  [AmbiguousConstructPreference.USE_SMALLER_FRAGMENT_IN_FIRST_BIN]: 'Use smaller fragment in first bin',
};

export function HomologyParametersControls(props: HomologyParametersControlsProps): JSX.Element {
  return (
    <AssemblyParametersControlsLayout
      controls={[
        <InputWrapper
          id="ambiguousConstructPreference"
          label="Ambiguous construct preference"
          labelStyle={LabelStyle.LABEL}
          isRequired={true}
          help={
            <span>
              In cases where the sequences you wish to assemble could create two distinct constructs,
              Benchling will create the construct that uses the larger/smaller fragment in the first bin. This
              can apply when assembling two circular sequences, since each sequence produces two fragments.
            </span>
          }
          helpInteraction={FloatingInteraction.HOVER}
        >
          <SelectDropdown
            className={css(styles.ambiguousConstructPreferenceDropdown)}
            displayName={
              AMBIGUOUS_CONSTRUCT_PREFERENCE_TO_DISPLAY[props.homologyParameters.ambiguousConstructPreference]
            }
            menu={
              <Menu width={MenuWidth.FIT_CONTENT}>
                <Option
                  key={AmbiguousConstructPreference.USE_LARGER_FRAGMENT_IN_FIRST_BIN}
                  value={AmbiguousConstructPreference.USE_LARGER_FRAGMENT_IN_FIRST_BIN}
                  onSelect={(value: AmbiguousConstructPreference.USE_LARGER_FRAGMENT_IN_FIRST_BIN) => {
                    props.patchHomologyParameters({ambiguousConstructPreference: value});
                  }}
                >
                  {
                    AMBIGUOUS_CONSTRUCT_PREFERENCE_TO_DISPLAY[
                      AmbiguousConstructPreference.USE_LARGER_FRAGMENT_IN_FIRST_BIN
                    ]
                  }
                </Option>
                <Option
                  key={AmbiguousConstructPreference.USE_SMALLER_FRAGMENT_IN_FIRST_BIN}
                  value={AmbiguousConstructPreference.USE_SMALLER_FRAGMENT_IN_FIRST_BIN}
                  onSelect={(value: AmbiguousConstructPreference.USE_SMALLER_FRAGMENT_IN_FIRST_BIN) => {
                    props.patchHomologyParameters({ambiguousConstructPreference: value});
                  }}
                >
                  {
                    AMBIGUOUS_CONSTRUCT_PREFERENCE_TO_DISPLAY[
                      AmbiguousConstructPreference.USE_SMALLER_FRAGMENT_IN_FIRST_BIN
                    ]
                  }
                </Option>
              </Menu>
            }
          />
        </InputWrapper>,
      ]}
    />
  );
}

const styles = StyleSheet.create({
  ambiguousConstructPreferenceDropdown: {
    width: '250px',
  },
});
